import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Alert,
  Button
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Html5Qrcode, Html5QrcodeSupportedFormats, Html5QrcodeScannerState } from 'html5-qrcode';

interface BarcodeScannerProps {
  open: boolean;
  onClose: () => void;
  onBarcodeScanned: (barcode: string) => void;
}

export const BarcodeScanner: React.FC<BarcodeScannerProps> = ({
  open,
  onClose,
  onBarcodeScanned,
}) => {
  const [error, setError] = useState<string | null>(null);
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const scannerContainerId = 'reader-container';
  const isMountedRef = useRef(false);

  // 1. Temizleme ve Durdurma Fonksiyonu (Promise Zinciri ile Güvenli)
  const stopScanner = async () => {
    if (scannerRef.current) {
      try {
        const state = scannerRef.current.getState();
        if (state === Html5QrcodeScannerState.SCANNING || state === Html5QrcodeScannerState.PAUSED) {
          await scannerRef.current.stop();
        }
      } catch (err) {
        console.warn("Durdurma hatası:", err);
      } finally {
        // html5-qrcode instance'ı temizlemiyoruz, sadece durduruyoruz.
        // Instance'ı clear() etmek DOM sorunlarına yol açabilir.
      }
    }
  };

  // 2. Başlatma Fonksiyonu
  const startScanner = useCallback(async () => {
    // 100ms gecikme ile DOM'un hazır olduğundan emin ol
    await new Promise(r => setTimeout(r, 100));

    const container = document.getElementById(scannerContainerId);
    if (!container) return;

    try {
      // Eğer zaten çalışıyorsa önce durdur
      await stopScanner();

      // Yeni instance oluştur (Eğer yoksa)
      if (!scannerRef.current) {
        scannerRef.current = new Html5Qrcode(scannerContainerId, { 
            verbose: false 
        });
      }

      // Hangi formatları desteklesin? (Hız için sadece önemliler)
      const config = {
        fps: 10, // iOS için 10-15 FPS idealdir, fazlası donma yapar
        qrbox: { width: 250, height: 150 }, // Sabit boyut (hesaplama hatasını önler)
        aspectRatio: 1.0,
        disableFlip: true, // Performans artırır
        formatsToSupport: [
            Html5QrcodeSupportedFormats.QR_CODE,
            Html5QrcodeSupportedFormats.EAN_13,
            Html5QrcodeSupportedFormats.CODE_128,
        ]
      };

      await scannerRef.current.start(
        { facingMode: "environment" }, // Arka kamera
        config,
        (decodedText) => {
          // Başarılı okuma
          onBarcodeScanned(decodedText);
          stopScanner().then(() => onClose());
        },
        (errorMessage) => {
          // Okuma hatalarını yut (Console kirliliğini önle)
        }
      );

      setError(null);

    } catch (err: any) {
      console.error("Kamera başlatma hatası:", err);
      setError(`Kamera hatası: ${err?.message || "Bilinmeyen hata"}`);
    }
  }, [onBarcodeScanned, onClose]);

  // 3. React Effect (Açılış/Kapanış Kontrolü)
  useEffect(() => {
    isMountedRef.current = true;

    if (open) {
      startScanner();
    } else {
      stopScanner();
    }

    // Component unmount olduğunda temizle
    return () => {
      isMountedRef.current = false;
      stopScanner();
    };
  }, [open, startScanner]);

  return (
    <Dialog
      open={open}
      onClose={() => { stopScanner().then(onClose); }}
      maxWidth="sm"
      fullWidth
      keepMounted={false} // Kapandığında DOM'dan tamamen silsin
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Barkod Okut</Typography>
        <IconButton onClick={() => { stopScanner().then(onClose); }} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, bgcolor: '#000', minHeight: 320, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        
        {/* Hata varsa göster */}
        {error && (
            <Box sx={{ p: 2, width: '100%', zIndex: 10 }}>
                <Alert severity="error">{error}</Alert>
                <Button 
                    variant="contained" 
                    color="primary" 
                    sx={{ mt: 1 }} 
                    onClick={() => { setError(null); startScanner(); }}
                >
                    Tekrar Dene
                </Button>
            </Box>
        )}

        {/* Tarayıcı Kutusu */}
        <div id={scannerContainerId} style={{ width: '100%', height: '100%' }} />

        {/* Bilgilendirme */}
        {!error && (
            <Typography variant="caption" sx={{ color: 'white', position: 'absolute', bottom: 10 }}>
                Kamerayı barkoda yaklaştırın
            </Typography>
        )}

      </DialogContent>
    </Dialog>
  );
};

export default BarcodeScanner;